import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { getMonthlyTaxReport } from "../../Services/authService";
import { useThemeContext } from "../../Context/ThemeContext";
import { useTranslation } from "react-i18next";
import {
    Box,
    IconButton, Button
} from '@mui/material';
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../Utils/constants";
import WestIcon from '@mui/icons-material/West';
import Card1 from '../../assets/Cards/Card1.webp'
import Card2 from '../../assets/Cards/Card2.webp'
import Card3 from '../../assets/Cards/Card3.webp'
import Card4 from '../../assets/Cards/Card4.webp'
import RevenuePieChart from "./RevenuePieChart";
import TaxTableReport from "./TaxTableReport";
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRange } from 'react-date-range';
import LocationPopup from '../Views/LocationPopup';
import { DropDownIcon } from '../../Components/Layouts/CustomIcons';
import useMediaQuery from '@mui/material/useMediaQuery';



const CardComponent = ({ background, value, label }) => (
    <div
        style={{
            backgroundImage: `url(${background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            borderRadius: "10px",
            color: "#000",
            padding: "1.5rem",
            transition: "transform 0.3s ease",
            width: "50%"
        }}
        className="sales-card"
    >
        <div className="sales-value">{value}</div>
        <div className="sales-label">{label}</div>
    </div>
);

const TaxReport = () => {
    const location = useLocation();
    const { mode } = useThemeContext();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const calendarRef = useRef(null);
    const calendarButtonRef = useRef(null);
    const month = location.state?.month || location.state?.franchise || "Default Month";

    const [taxReport, setTaxReport] = useState(null);
    const [cardData, setCardData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [machineNumbers, setMachineNumbers] = useState([]);
    // const [dateRange, setDateRange] = useState([{ key: 'selection', },]);
    const [dateRange, setDateRange] = useState(() => {
        if (month) {
            const firstDay = new Date(`${month}-01`);
            const lastDay = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0);
            return [{ key: 'selection', startDate: firstDay, endDate: lastDay }];
        }
        return [{ key: 'selection', startDate: new Date(), endDate: new Date() }];
    });

    const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [applyClicked, setApplyClicked] = useState(false);

    const backgroundCard = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const dropCircle = mode === 'dark' ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
    const dropIcon = mode === 'dark' ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
    const settingIconColor = mode === 'dark' ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;
    const btnColor1 = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;



    const isTablet = useMediaQuery('(max-width:850px)');
    const isMobile = useMediaQuery('(max-width:500px)');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const fetchTaxReport = async () => {
        setLoading(true);
        setError(null);
        try {
            if (month !== "Default Month") {
                const formatLocalDate = (date) => {
                    if (!date) return null;
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
                    const day = String(date.getDate()).padStart(2, "0");
                    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD
                };
                const startDate = dateRange[0]?.startDate ? formatLocalDate(new Date(dateRange[0].startDate)) : null;
                const endDate = dateRange[0]?.endDate ? formatLocalDate(new Date(dateRange[0].endDate)) : null;
                const data = await getMonthlyTaxReport(month, startDate, endDate, machineNumbers);
                const updatedCardData = [
                    { value: `€ ${data.monthlyReport.grossAmount.toFixed(2)}`, label: t('taxes.grossAmount'), background: Card1 },
                    { value: `€ ${data.monthlyReport.netAmount.toFixed(2)}`, label: t('taxes.netAmount'), background: Card2 },
                    { value: data.monthlyReport.totalTransactions, label: t('transactions.transactions'), background: Card3 },
                    { value: `€ ${data.monthlyReport.taxableAmount.toFixed(2)}`, label: t('taxes.taxableAmount'), background: Card4 },
                ];
                setTaxReport(data);
                setCardData(updatedCardData)
            } else {
                setTaxReport(null);
            }
        } catch (err) {
            setError(t('taxes.Failedloaddata'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTaxReport();
    }, [month, machineNumbers, applyClicked, i18n.language]);

    const handleBackClick = () => {
        navigate(ROUTES.TAXES)
    };

    const handleLocationClick = () => setIsPopupOpen(true);
    const handleLocationPopupClose = () => setIsPopupOpen(false);
    const handleDateChange = (item) => {
        setDateRange([item.selection]);
    };

    const handleCalendarToggle = () => {
        if (calendarButtonRef.current) {
            const rect = calendarButtonRef.current.getBoundingClientRect();
            setCalendarPosition({ top: rect.bottom + window.scrollY + 10, left: rect.left + window.scrollX });
        }
        setIsCalendarOpen((prev) => !prev);
    };

    const handleApply = () => {
        setApplyClicked(!applyClicked)
        setIsCalendarOpen(false)
    };

    // Close the calendar when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target) &&
                calendarButtonRef.current &&
                !calendarButtonRef.current.contains(event.target)
            ) {
                setIsCalendarOpen(false);
            }
        }

        if (isCalendarOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isCalendarOpen]);

    const handleLocationSubmit = (locations, isAllSelected) => {
        const selectedMachineIds = [];
        locations.forEach((location) => {
            location.machines.forEach((machine) => {
                selectedMachineIds.push(machine.machineId);
            });
        });
        setMachineNumbers(selectedMachineIds); // Store machine numbers in the state
    };
    return (
        <div className="main-container">
            <Box className='back-calender-sec' sx={{ display: "flex", justifyContent: "space-between" }}>
                {/* Back Button and Title */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        sx={{
                            color: btnColor,
                            backgroundColor: backgroundCard,
                            padding: '0.6rem',
                            borderRadius: '50%',
                            marginRight: '1rem',
                            '&:hover': { backgroundColor: backgroundCard },
                        }}
                        onClick={handleBackClick}
                    >
                        <WestIcon fontSize="small" />
                    </IconButton>
                    <h2 className="addmachine-text-heading">
                        {`${new Date(month).toLocaleString("default", { month: "long" })} ${t('taxes.MonthReport')}`}
                    </h2>
                </Box>

                <Box className='calender-location-sec' sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    {/* Location Selector acting as a button */}
                    <Button className='add-machine'
                        onClick={handleLocationClick}
                        sx={{
                            borderRadius: "24px",
                            textAlign: "center",
                            padding: "9px 20px",
                            display: "flex",
                            alignItems: "center",
                            color: settingIconColor,
                            background: backgroundCard,
                            textTransform: 'none',
                            justifyContent: "space-between",
                            gap: "8px",
                            minWidth: "160px",
                        }}
                        endIcon={<DropDownIcon color1={dropCircle} color2={dropIcon} />}
                    >
                        {selectedLocation ? selectedLocation : t("transactions.selectLocation")}
                    </Button>
                    {/* Location Popup */}
                    <LocationPopup
                        open={isPopupOpen}
                        onClose={handleLocationPopupClose}
                        selectedMachineLocations={selectedLocation}
                        onSubmit={handleLocationSubmit}
                    />

                    {/* Calendar Icon */}
                    <IconButton
                        ref={calendarButtonRef}
                        aria-label="select-date"
                        onClick={handleCalendarToggle}
                        sx={{
                            background: backgroundCard,
                            borderRadius: "50%",
                            padding: "10px",
                            color: dropIcon,
                        }}
                    >
                        <CalendarTodayOutlinedIcon style={{ fontSize: "1rem" }} />
                    </IconButton>
                    {/* Date Range Picker */}
                    {isCalendarOpen && (
                        <div className='calender-modal' ref={calendarRef} style={{
                            background: backgroundCard,
                            position: 'absolute',
                            top: `${calendarPosition.top}px`,
                            right: '10px',
                            zIndex: 1000, display: "flex", flexDirection: "column", borderRadius: "12px"
                        }}>
                            <DateRange
                                editableDateInputs={true}
                                onChange={handleDateChange}
                                moveRangeOnFirstSelection={false}
                                ranges={dateRange}
                                minDate={new Date(`${month}-01`)} // Start date of the current month
                                maxDate={new Date(new Date(`${month}-01`).getFullYear(), new Date(`${month}-01`).getMonth() + 1, 0)} // End date of the current month
                                style={{
                                    borderRadius: '12px',
                                    padding: '8px',
                                }}
                            />
                            <div style={{ textAlign: "right", margin: "1rem 0.5rem" }}>
                                <button type="button" style={{ background: btnColor1, padding: "10px 15px", fontSize: "15px" }} className='save-btn' onClick={handleApply}>{t('sales_analytics.apply')}</button>
                            </div>
                        </div>
                    )}
                </Box>
            </Box>

            <Box className="taxe-first-sectiom" sx={{ display: "flex", gap: "12px", alignItems: "center", width: "100%", mt: "15px" }}>
                <Box className="revenue-cards" sx={{ width: "50%" }}>
                    {[0, isTablet ? 4 : 2].map((startIndex) => (
                        <Box className="taxe-cards-sectiom" key={startIndex} sx={{ display: "flex", gap: "12px", alignItems: "center", width: "100%", mt: startIndex === 2 ? "12px" : 0 }}>
                            {cardData.slice(startIndex, startIndex + isTablet ? 4 : 2).map((card, index) => (
                                <CardComponent key={index} background={card.background} value={card.value} label={card.label} />
                            ))}
                        </Box>
                    ))}
                </Box>

                {/* Right side: Pie Chart for Revenue by Payment Type */}
                <Box className="revenue-sec" sx={{ width: "50%" }}>
                    <RevenuePieChart revenueData={taxReport?.revenueByPaymentType || {}} />
                </Box>
            </Box>

            <TaxTableReport month={month} selectedLocations={machineNumbers} dateRange={dateRange} applyClicked={applyClicked} cardData={cardData} />
        </div>
    );
};

export default TaxReport;
